/**
 * Stock Management API Service
 * 
 * Backend API endpoints:
 * - GET /api/stock-categories
 * - POST /api/stock-categories
 * - DELETE /api/stock-categories/:id
 * - GET /api/stocks
 * - POST /api/stocks
 * - PATCH /api/stocks/:id
 * - DELETE /api/stocks/:id
 * - PATCH /api/stocks/:id/quantity
 * - GET /api/stocks/:id/history
 */

import api from './api';

// ============ Categories ============

/**
 * Get all stock categories
 * @returns {Promise<{categories: Array}>}
 */
export const getCategories = async () => {
    const response = await api.get('/stock-categories');
    return response.categories || [];
};

/**
 * Create a new category
 * @param {string} name - Category name
 * @returns {Promise<Object>}
 */
export const createCategory = async (name) => {
    const response = await api.post('/stock-categories', { name });
    return response;
};

/**
 * Delete a category
 * @param {number} id - Category ID
 * @returns {Promise<void>}
 */
export const deleteCategory = async (id) => {
    await api.delete(`/stock-categories/${id}`);
};

/**
 * Update a category
 * @param {number} id - Category ID
 * @param {string} name - New category name
 * @returns {Promise<Object>}
 */
export const updateCategory = async (id, name) => {
    const response = await api.patch(`/stock-categories/${id}`, { name });
    return response;
};

// ============ Stocks ============

/**
 * Get all stocks with optional category filter
 * @param {number} [categoryId] - Optional category filter
 * @returns {Promise<{stocks: Array}>}
 */
export const getStocks = async (categoryId) => {
    const params = {};
    if (categoryId !== undefined && categoryId !== null && categoryId !== 'all') {
        params.category_id = categoryId;
    }
    const response = await api.get('/stocks', params);
    return response.stocks || [];
};

/**
 * Create a new stock item
 * @param {Object} data - Stock data
 * @returns {Promise<Object>}
 */
export const createStock = async (data) => {
    const response = await api.post('/stocks', data);
    return response.stock || response;
};

/**
 * Update a stock item
 * @param {number} id - Stock ID
 * @param {Object} data - Updated data
 * @returns {Promise<Object>}
 */
export const updateStock = async (id, data) => {
    const response = await api.patch(`/stocks/${id}`, data);
    return response.stock || response;
};

/**
 * Delete a stock item
 * @param {number} id - Stock ID
 * @returns {Promise<void>}
 */
export const deleteStock = async (id) => {
    await api.delete(`/stocks/${id}`);
};

/**
 * Adjust stock quantity
 * @param {number} id - Stock ID
 * @param {number} change - Quantity change (positive or negative)
 * @param {string} [reason] - Optional reason for the change
 * @returns {Promise<{id: number, quantity: number, last_updated: string}>}
 */
export const adjustQuantity = async (id, change, reason = null) => {
    const data = { change };
    if (reason) data.reason = reason;
    const response = await api.patch(`/stocks/${id}/quantity`, data);
    return response;
};

/**
 * Get stock history
 * @param {number} id - Stock ID
 * @returns {Promise<{history: Array}>}
 */
export const getStockHistory = async (id) => {
    const response = await api.get(`/stocks/${id}/history`);
    return response.history || [];
};

const stockApi = {
    getCategories,
    createCategory,
    updateCategory,
    deleteCategory,
    getStocks,
    createStock,
    updateStock,
    deleteStock,
    adjustQuantity,
    getStockHistory,
};

export default stockApi;
